import { useEffect, useState } from 'react'
import { ClipboardList } from 'lucide-react'
import Button from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import Drawer from '../../components/Drawer'
import { FormBody, FormCols } from '../../components/Form'
import FormRow from '../../components/FormRow'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import ExamRules from './ExamRules'
import { optionsForCourse, optionsForTopic, questionsAtLevel } from './examsService'
import { strings as S } from './strings'
import './exams.css'

const EMPTY = {
  courseId: '',
  nameAr: '',
  nameEn: '',
  passLimit: '',
  tries: '',
  forceWatch: false,
  hideAnswers: false,
  preventIfSucceeded: false,
  rewatchOnFailPercentage: '',
  rulesIds: '',
}

const EMPTY_RULE = { topicId: '', levelId: '', count: '' }

/**
 * One exam: its settings, and the rules that compose its questions.
 *
 * THE OPTION LISTS CASCADE, and each step is a round trip because only the server knows
 * the answer: which topics a course covers, which difficulties that topic has questions
 * at, and how many exist at the pairing. That last number is the cap on a rule — asking
 * for six questions where four exist is what this stops.
 *
 * @param {object} settings the `ExamSettingsDto` the caller already fetched, so opening an
 *        edit does not re-ask for what it just read.
 * @param {object} [course] the course this exam belongs to, when the drawer is opened FROM
 *        that course rather than from the exams list — `{ id, name }`. The legacy's
 *        `showExamForm(courseId)` is always about one course, so there is nothing to pick:
 *        the field states which course instead of offering a list that could be changed to
 *        the wrong one.
 */
export default function ExamFormDrawer({ open, settings, course, onClose, onSave }) {
  const [values, setValues] = useState(EMPTY)
  const [rules, setRules] = useState([])
  const [draft, setDraft] = useState(EMPTY_RULE)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  /** Narrowed as the draft rule is filled in: topics by course, levels by topic. */
  const [topics, setTopics] = useState([])
  const [levels, setLevels] = useState([])
  const [available, setAvailable] = useState(null)

  useEffect(() => {
    if (!open) return

    setErrors({})
    setDraft(EMPTY_RULE)
    setAvailable(null)
    setValues(
      settings?.exam
        ? { ...EMPTY, ...settings.exam, courseId: course?.id || settings.exam.courseId }
        : { ...EMPTY, ...(course ? { courseId: course.id } : null) },
    )
    setRules(settings?.rules ?? [])
    setTopics(settings?.topics ?? [])
    setLevels(settings?.levels ?? [])
  }, [open, settings, course])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  /* Choosing a course narrows the topics to the ones it covers. */
  const chooseCourse = async (courseId) => {
    set('courseId')(courseId)
    setDraft(EMPTY_RULE)
    setAvailable(null)

    const next = await optionsForCourse(courseId).catch(() => null)
    if (next) setTopics(next.topics)
  }

  const chooseTopic = async (topicId) => {
    setDraft((previous) => ({ ...previous, topicId, levelId: '', count: '' }))
    setAvailable(null)

    const next = await optionsForTopic(topicId).catch(() => null)
    if (next) setLevels(next.levels)
  }

  const chooseLevel = async (levelId) => {
    setDraft((previous) => ({ ...previous, levelId, count: '' }))

    const next = await questionsAtLevel(draft.topicId, levelId).catch(() => null)
    setAvailable(next?.questionsAvailable ?? null)
  }

  const addRule = () => {
    if (!draft.topicId || !draft.levelId || !draft.count) return
    if (available != null && Number(draft.count) > available) {
      return setErrors((previous) => ({ ...previous, rule: S.rules.tooMany }))
    }

    setRules((previous) => [
      ...previous,
      {
        /* Saved rule IDs are "category,difficulty". Ignore those when allocating distinct
           negative IDs so removing a draft never reuses another row's identity. */
        id: Math.min(0, ...previous.map((rule) => Number(rule.id)).filter(Number.isFinite)) - 1,
        topicId: Number(draft.topicId),
        topic: topics.find((option) => String(option.value) === String(draft.topicId))?.label ?? '',
        levelId: Number(draft.levelId),
        level: levels.find((option) => String(option.value) === String(draft.levelId))?.label ?? '',
        count: Number(draft.count),
      },
    ])

    setDraft(EMPTY_RULE)
    setAvailable(null)
    setErrors((previous) => ({ ...previous, rule: undefined }))
  }

  const submit = async (event) => {
    event.preventDefault()

    const next = {}
    if (!values.courseId) next.courseId = S.form.required
    if (!values.nameAr.trim()) next.nameAr = S.form.required
    if (!values.nameEn.trim()) next.nameEn = S.form.required
    if (!rules.length) next.rule = S.rules.empty
    setErrors(next)
    if (Object.keys(next).length > 0) return

    setSaving(true)
    /* `RulesIds` is the comma-separated string the command takes back. Only saved rules
       have an id worth sending; the negatives are new and the server creates them. */
    await onSave({
      ...values,
      rules,
      rulesIds: rules.map((rule) => `${rule.topicId},${rule.levelId},${rule.count}`).join(';'),
    })
    setSaving(false)
  }

  return (
    <Drawer
      dialog
      open={open}
      size="lg"
      icon={ClipboardList}
      title={values.id ? S.form.editTitle : S.form.createTitle}
      onClose={onClose}
      footer={
        <>
          <Button type="submit" form="exam-form" disabled={saving}>
            {S.save}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {S.close}
          </Button>
        </>
      }
    >
      <form id="exam-form" onSubmit={submit} noValidate>
        <FormBody>
          <FormRow label={S.form.course} error={errors.courseId}>
            {(id) =>
              course ? (
                <TextField id={id} value={course.name} readOnly />
              ) : (
                <Select
                  id={id}
                  value={values.courseId}
                  onChange={chooseCourse}
                  options={settings?.courses ?? []}
                  invalid={!!errors.courseId}
                />
              )
            }
          </FormRow>

          <FormCols>
            <FormRow label={S.form.nameAr} error={errors.nameAr}>
              {(id) => (
                <TextField
                  id={id}
                  value={values.nameAr}
                  onChange={set('nameAr')}
                  invalid={!!errors.nameAr}
                />
              )}
            </FormRow>

            <FormRow label={S.form.nameEn} error={errors.nameEn}>
              {(id) => (
                <TextField
                  id={id}
                  dir="ltr"
                  value={values.nameEn}
                  onChange={set('nameEn')}
                  invalid={!!errors.nameEn}
                />
              )}
            </FormRow>
          </FormCols>

          <FormCols>
            <FormRow label={S.form.passLimit}>
              {(id) => (
                <TextField id={id} dir="ltr" value={String(values.passLimit ?? '')} onChange={set('passLimit')} />
              )}
            </FormRow>

            <FormRow label={S.form.tries}>
              {(id) => (
                <TextField id={id} dir="ltr" value={String(values.tries ?? '')} onChange={set('tries')} />
              )}
            </FormRow>
          </FormCols>

          <FormRow label={S.form.rewatch}>
            {(id) => (
              <TextField
                id={id}
                dir="ltr"
                value={String(values.rewatchOnFailPercentage ?? '')}
                onChange={set('rewatchOnFailPercentage')}
              />
            )}
          </FormRow>

          <Checkbox checked={values.forceWatch} onChange={set('forceWatch')}>
            {S.form.forceWatch}
          </Checkbox>
          <Checkbox checked={values.hideAnswers} onChange={set('hideAnswers')}>
            {S.form.hideAnswers}
          </Checkbox>
          <Checkbox checked={values.preventIfSucceeded} onChange={set('preventIfSucceeded')}>
            {S.form.preventIfSucceeded}
          </Checkbox>

          <ExamRules
            rules={rules}
            onRemove={(index) => setRules((previous) => previous.filter((_, i) => i !== index))}
            draft={draft}
            topics={topics}
            levels={levels}
            available={available}
            error={errors.rule}
            onTopic={chooseTopic}
            onLevel={chooseLevel}
            onCount={(count) => setDraft((previous) => ({ ...previous, count }))}
            onAdd={addRule}
          />
        </FormBody>
      </form>
    </Drawer>
  )
}
